import {useMutation} from '@tanstack/react-query';
import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';

import {ToastNotification} from 'app/modules/Notification';
import {api} from 'app/modules/Remote';
import {ApiBulkOrderVerificationResponse} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiProject} from 'app/modules/Remote/Project';
import {useOrderableScenesLoader} from 'app/providers/OrderableScenesProvider';

import {orderBulkImagery} from './orderImageryUtils';

export function useVerifyOrderImageryBulk({
  featureCollection,
  project,
  features,
  sensingTime,
  sourceId,
  enabled,
}: {
  featureCollection: ApiFeatureCollection;
  project: ApiProject;
  features: number[];
  sensingTime: string;
  sourceId: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [
      'verifyOrderImageryBulk',
      featureCollection.id,
      project.id,
      features,
      sensingTime,
      sourceId,
    ],
    enabled,
    queryFn: () => {
      return api.featureCollections
        .features(featureCollection.id)
        .verifyOrderImageryBulk(project.id, features, sensingTime!, sourceId);
    },
    select: (response) => {
      return response.get('data').toJS() as ApiBulkOrderVerificationResponse;
    },
  });
}

export function usePlaceBulkOrder({
  featureCollection,
  project,
  sensingTime,
  sourceId,
}: {
  featureCollection: ApiFeatureCollection;
  project: ApiProject;
  sensingTime: string;
  sourceId: string;
}) {
  return useMutation({
    mutationFn: async ({
      featureIds,
      expectedCostInCents,
      pushNotification,
    }: {
      featureIds: number[];
      expectedCostInCents: number;
      pushNotification: (p: ToastNotification) => void;
    }) => {
      return orderBulkImagery(
        project.id,
        featureCollection.id,
        featureIds,
        sourceId,
        sensingTime,
        expectedCostInCents,
        pushNotification
      );
    },
  });
}

export function useBulkSensingTime(
  featureCollectionId: number,
  projectId: string,
  featureIds: number[],
  sourceId: string,
  sensingTime?: string
) {
  const [getOrderableScenes] = useOrderableScenesLoader(featureCollectionId, projectId);
  const orderableScenesStatusMaybe = getOrderableScenes(featureIds[0]);

  return useMemo(() => {
    if (sensingTime) {
      return sensingTime;
    }
    if (orderableScenesStatusMaybe.status === 'some') {
      const orderableScenes = orderableScenesStatusMaybe.value;
      // These should already be sorted by sensingTime (in lens_overview.py at
      // paid_scenes), so we can just take the first one
      const latestOrderableSceneForSource = orderableScenes.find((scene) => {
        return scene?.get('sourceId') === sourceId;
      });
      return latestOrderableSceneForSource?.get('sensingTime');
    }
    return null;
  }, [orderableScenesStatusMaybe.status, orderableScenesStatusMaybe.value, sensingTime, sourceId]);
}
